/**
 * Delivery-schedule helpers.
 *
 * A plan such as "Sunday Curd" is a product with a schedule attached. A product with no
 * schedule delivers every day, which is why everything that existed before plans keeps working.
 *
 * Day-of-week is 0=Monday..6=Sunday everywhere in Vendora, and the weekly mask uses bit0=Monday
 * to match. This is the mirror of app/schedule.py — the strings below are shown to vendors on
 * plan cards, the delivery round and invoices, so the two files must agree word for word.
 */

export const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const WEEKDAY_LONG = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const WEEKDAY_INITIAL = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export const ALL_DAYS_MASK = 127;

export const maskToDays = (mask) =>
    WEEKDAY_SHORT.map((_, i) => i).filter((i) => ((Number(mask) || 0) >> i) & 1);

export const daysToMask = (days) => (days || []).reduce((m, d) => m | (1 << Number(d)), 0);

const ordinal = (n) => {
    if (n % 100 >= 10 && n % 100 <= 20) return `${n}th`;
    return `${n}${({ 1: 'st', 2: 'nd', 3: 'rd' })[n % 10] || 'th'}`;
};

/**
 * Plain-language delivery days: "Every day", "Every 2 days", "Every Sunday", "Mon & Thu",
 * "On the 5th". Accepts a product/plan object or a bare {type, value} shape.
 */
export function formatSchedule(planOrShape) {
    if (!planOrShape) return 'Every day';
    const type = planOrShape.f_schedule_type || planOrShape.type || 'daily';
    const value = Number(planOrShape.f_schedule_value ?? planOrShape.value ?? 1);

    if (type === 'daily') return 'Every day';
    if (type === 'interval') return value <= 1 ? 'Every day' : `Every ${value} days`;
    if (type === 'monthly') return `On the ${ordinal(value)}`;
    if (type === 'weekly') {
        const days = maskToDays(value);
        if (days.length === 0) return 'No days chosen';
        if (days.length === 7) return 'Every day';
        if (days.length === 1) return `Every ${WEEKDAY_LONG[days[0]]}`;
        const names = days.map((d) => WEEKDAY_SHORT[d]);
        return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
    }
    return 'Every day';
}

/** Does this schedule deliver on the given Date? Mirrors is_delivery_day() in app/schedule.py. */
export function isDeliveryDay(shape, d) {
    if (!shape) return true;
    const type = shape.f_schedule_type || shape.type || 'daily';
    const value = Number(shape.f_schedule_value ?? shape.value ?? 1);
    const anchor = shape.f_anchor_date || shape.anchor;

    if (type === 'daily') return true;
    // JS getDay() is 0=Sunday; shift to 0=Monday to match the rest of the app.
    const weekday = (d.getDay() + 6) % 7;

    if (type === 'weekly') return Boolean(value & (1 << weekday));
    if (type === 'monthly') {
        const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
        return d.getDate() === Math.min(value, lastDay);
    }
    if (type === 'interval') {
        if (value <= 1 || !anchor) return true;
        const a = new Date(`${anchor}T00:00:00`);
        const delta = Math.round((d - a) / 86400000);
        return delta >= 0 && delta % value === 0;
    }
    return true;
}

/** True when this product is a plan (has a parent product it prices off). */
export const isPlan = (p) => Boolean(p && p.f_parent_business_product_id);

/** Schedule shape from a product/plan, ready to hand to <SchedulePicker/>. */
export const scheduleShapeOf = (p) => ({
    f_schedule_type: p?.f_schedule_type || 'daily',
    f_schedule_value: p?.f_schedule_value ?? 1,
    f_anchor_date: p?.f_anchor_date || null,
});
